(() => {
  const mobileViewport = window.matchMedia("(max-width: 575px)");
  const playRequests = new WeakMap();
  const posterLoads = new WeakMap();

  const getActiveVideo = (slide) => {
    if (!slide) return null;
    const mobileVideo = slide.querySelector(".slideshow__video-item--mobile");
    return mobileViewport.matches && mobileVideo
      ? mobileVideo
      : slide.querySelector(".slideshow__video-item--desktop");
  };

  const waitForPoster = (poster) => {
    if (!poster || poster.complete) return Promise.resolve();
    if (!posterLoads.has(poster)) {
      posterLoads.set(poster, new Promise((resolve) => {
        const finish = () => {
          poster.removeEventListener("load", finish);
          poster.removeEventListener("error", finish);
          posterLoads.delete(poster);
          resolve();
        };
        poster.addEventListener("load", finish, { once: true });
        poster.addEventListener("error", finish, { once: true });
      }));
    }
    return posterLoads.get(poster);
  };

  const stopVideo = (slider) => {
    playRequests.delete(slider);
    slider.querySelectorAll(".slideshow__video video").forEach((video) => {
      video.pause();
      video.closest(".slideshow__video").classList.remove("is-playing");
    });
  };

  const hydrateVideo = (video) => {
    const sources = Array.from(video.querySelectorAll("source[data-src]"));
    const mp4Sources = sources.filter((source) =>
      source.type === "video/mp4" && Number(source.dataset.width) > 0
    ).sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
    if (mp4Sources.length) {
      const ratio = Number(mp4Sources[0].dataset.width) / Number(mp4Sources[0].dataset.height);
      const displayedWidth = Math.max(video.clientWidth, video.clientHeight * (ratio || 1));
      // Background motion needs fewer pixels than foreground product imagery.
      // Choose an existing rendition; never construct a Shopify CDN video URL.
      const targetWidth = mobileViewport.matches
        ? Math.min(720, displayedWidth * Math.min(window.devicePixelRatio || 1, 1.5))
        : Math.min(1920, displayedWidth);
      const source = mp4Sources.find((item) => Number(item.dataset.width) >= targetWidth)
        || mp4Sources[mp4Sources.length - 1];
      source.src = source.dataset.src;
    } else {
      sources.forEach((source) => { source.src = source.dataset.src; });
    }
    video.dataset.hydrated = "true";
    video.load();
  };

  const playVideo = async (slider) => {
    stopVideo(slider);
    if (!slider.swiper || slider.dataset.inViewport !== "true" || document.hidden) return;

    const swiper = slider.swiper;
    const activeSlide = swiper.slides[swiper.activeIndex];
    const video = getActiveVideo(activeSlide);
    if (!video) return;

    swiper.slides.forEach((slide) => {
      if (slide === activeSlide) return;
      slide.querySelectorAll(".slideshow__video video").forEach((inactiveVideo) => {
        if (inactiveVideo.readyState > 0) inactiveVideo.currentTime = 0;
      });
    });

    const request = {};
    playRequests.set(slider, request);
    const wrapper = video.closest(".slideshow__video");
    await waitForPoster(wrapper.querySelector(".slideshow__video-poster img"));
    // Let the poster paint before a video download competes for bandwidth.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (playRequests.get(slider) !== request || document.hidden) return;

    if (video.dataset.hydrated !== "true") {
      video.addEventListener("playing", () => {
        if (!video.paused && getActiveVideo(swiper.slides[swiper.activeIndex]) === video) {
          wrapper.classList.add("is-playing");
        }
      });
      hydrateVideo(video);
    }
    video.play().catch(() => {});
  };

  const initSlider = (section) => {
    if (!section || !section.classList.contains("section-slideshow")) return;

    const box = section.querySelector(".slideshow");

    if (box) {
      const swiperParams = {
        init: false,
        speed: 800,
        autoHeight: false,
        allowTouchMove: true,
        watchSlidesProgress: true,
        preventInteractionOnTransition: true,
        mousewheel: {
          forceToAxis: true,
        },
      };

      const changeColorScheme = (swiper) => {
        const activeIndex = swiper.activeIndex;
        const activeSlide = swiper.slides[activeIndex];
        if (!activeSlide) return;
        const colorScheme = activeSlide.dataset.colorScheme;
        const exploreBtn = box.querySelector(".slideshow__explore-btn");

        const changeItems = [
          swiper.navigation?.nextEl,
          swiper.navigation?.prevEl,
          swiper.pagination?.el,
          ...(exploreBtn ? [exploreBtn] : []),
        ].filter(Boolean);

        changeItems.forEach((item) => {
          let classNames = item.getAttribute("class") || "";
          classNames = classNames.replace(/color-background-\d+/g, "");
          item.setAttribute("class", classNames);
          item.classList.add(colorScheme);
        });
      };

      if (box.getAttribute("data-autoplay") === "true") {
        swiperParams.autoplay = {
          disableOnInteraction: true,
          //pauseOnMouseEnter: true,
        };
      }

      if (box.getAttribute("data-loop") === "true") {
        swiperParams.loop = true;
        swiperParams.loopPreventsSliding = false;
      }

      if (box.getAttribute("data-pagination") === "true") {
        const paginationEl = box.querySelector(".swiper-pagination");

        swiperParams.pagination = {
          el: paginationEl,
          type: "bullets",
          clickable: true,
        };
      }

      // Uncomment if you want navigation buttons
      /*
      if (box.getAttribute("data-navigation") === "true") {
        const prevBtn = box.querySelector(".swiper-button-prev");
        const nextBtn = box.querySelector(".swiper-button-next");

        swiperParams.navigation = {
          nextEl: nextBtn,
          prevEl: prevBtn,
        };
      }
      */

      if (box.getAttribute("data-parallax") === "true") {
        swiperParams.parallax = true;
      }

      if (box.getAttribute("data-animation-type") === "fade") {
        swiperParams.effect = "fade";
      } else {
        swiperParams.effect = "slide";
      }

      if (box.getAttribute("data-full-width") === "false") {
        swiperParams.breakpoints = {
          990: {
            spaceBetween: 16,
          },
        };
      }

      if (box.getAttribute("data-layout") === "overlay") {
        const sliderOverlay = box.querySelector(".slideshow__swiper--overlay");

        if (sliderOverlay) {
          const swiperOverlay = new Swiper(sliderOverlay, swiperParams);

          swiperOverlay.on("slideChange", () => {
            changeColorScheme(swiperOverlay);
            playVideo(sliderOverlay);
          });

          swiperOverlay.on("init", () => {
            changeColorScheme(swiperOverlay);
            playVideo(sliderOverlay);
          });

          swiperOverlay.init();
        }
      } else if (box.getAttribute("data-layout") === "split_screen") {
        const sliderText = box.querySelector(".slideshow__swiper--text");
        const sliderMedia = box.querySelector(".slideshow__swiper--media");

        if (box.getAttribute("data-full-width") === "false") {
          swiperParams.spaceBetween = 8;
          swiperParams.breakpoints = {
            990: {
              spaceBetween: 16,
            },
          };
        }

        if (sliderText && sliderMedia) {
          const swiperText = new Swiper(sliderText, swiperParams);

          const swiperMedia = new Swiper(sliderMedia, {
            ...swiperParams,
            autoplay: false,
            pagination: false,
            navigation: false,
            parallax: false,
          });

          swiperText.on("slideChange", () => {
            changeColorScheme(swiperText);
          });

          swiperMedia.on("slideChange", () => {
            playVideo(sliderMedia);
          });

          swiperText.on("init", () => {
            changeColorScheme(swiperText);
            playVideo(sliderMedia);
          });

          swiperText.init();
          swiperMedia.init();
          swiperText.controller.control = swiperMedia;
          swiperMedia.controller.control = swiperText;
        }
      }
    }
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("section-slideshow")) return;

    const box = section.querySelector(".slideshow");

    let slider;
    if (box && box.getAttribute("data-layout") === "overlay") {
      slider = section.querySelector(".slideshow__swiper");
    } else if (box && box.getAttribute("data-layout") === "split_screen") {
      slider = section.querySelector(".slideshow__swiper--media");
    }

    if (!slider) return;

    const autoplaySlider = box.getAttribute("data-layout") === "split_screen"
      ? section.querySelector(".slideshow__swiper--text")
      : slider;
    const updatePlayback = () => {
      if (slider.dataset.inViewport === "true" && !document.hidden) {
        playVideo(slider);
        if (box.getAttribute("data-autoplay") === "true") {
          autoplaySlider.swiper?.autoplay?.resume();
        }
      } else {
        stopVideo(slider);
        if (box.getAttribute("data-autoplay") === "true") {
          autoplaySlider.swiper?.autoplay?.pause();
        }
      }
    };
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        slider.dataset.inViewport = String(entry.isIntersecting);
        updatePlayback();
      });
    });

    sectionObserver.observe(slider);
    mobileViewport.addEventListener("change", updatePlayback);
    document.addEventListener("visibilitychange", updatePlayback);
    section.addEventListener("shopify:section:unload", () => {
      sectionObserver.disconnect();
      mobileViewport.removeEventListener("change", updatePlayback);
      document.removeEventListener("visibilitychange", updatePlayback);
      stopVideo(slider);
      slider.swiper?.destroy();
      if (autoplaySlider !== slider) autoplaySlider.swiper?.destroy();
    }, { once: true });
  };

  const initScrollToExplore = (section) => {
    if (!section || !section.classList.contains("section-slideshow")) return;

    const scrollBtn = section.querySelector(".slideshow__explore-btn");
    if (!scrollBtn) return;

    const nextSection = section.nextElementSibling;

    scrollBtn.addEventListener("click", () => {
      if (nextSection) {
        nextSection.scrollIntoView({ behavior: "smooth" });
        return;
      }

      section.scrollIntoView({ behavior: "smooth" });
    });

    scrollBtn.addEventListener("keydown", (event) => {
      if (nextSection && event.key === "Enter") {
        nextSection.scrollIntoView({ behavior: "smooth" });
        return;
      }

      section.scrollIntoView({ behavior: "smooth" });
    });
  };

  const initialize = (section) => {
    if (!section?.classList.contains("section-slideshow") || section.dataset.slideshowInitialized) return;
    section.dataset.slideshowInitialized = "true";
    initSlider(section);
    initSection(section);
    initScrollToExplore(section);
  };

  initialize(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();
